import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import {
  SITE,
  TEL_HREF,
  MAIL_HREF,
  trackEvent,
} from "@/siteConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVIZIO_OPTIONS = [
  "Cancello Automatico / Manuale",
  "Serranda Negozio / Box Garage",
  "Automazione / Motore Elettrico",
  "Serratura / Porta Blindata / Accesso",
  "Apertura porte / Fabbro urgente",
  "Pronto intervento elettrico",
  "Altro intervento urgente",
];

const CONTACT_CARDS = [
  {
    id: "telefono",
    icon: Phone,
    label: "Telefono · Linea H24",
    value: SITE.phoneDisplay,
    href: TEL_HREF,
    accent: "text-amber-400 border-amber-400/25 bg-amber-400/10",
  },
  {
    id: "email",
    icon: Mail,
    label: "Email",
    value: SITE.email,
    href: MAIL_HREF,
    accent: "text-zinc-300 border-white/15 bg-white/5",
    onClick: () => trackEvent("click_email", { cta_location: "contact_section" }),
  },
];

const initialForm = { nome: "", telefono: "", zona: "", servizio: "", messaggio: "" };

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      trackEvent("form_submit", { form: "contatto" });
      toast.success("Richiesta inviata correttamente", {
        description: "Ti ricontatteremo il prima possibile. Per urgenze immediate chiama ora.",
      });
      setForm(initialForm);
    } catch (err) {
      toast.error("Invio non riuscito", {
        description: "Riprova tra poco oppure chiamaci direttamente.",
      });
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-white/10 bg-[#0D1119] px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-amber-400/60 focus:ring-2 focus:ring-amber-400/20";

  return (
    <section
      id="contatti"
      data-testid="contact-section"
      className="scroll-mt-24 border-t border-white/[0.06] bg-[#0D1119] py-20 sm:py-28"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              overline="Contatti"
              title="Contattaci subito"
              lead="Per urgenze immediate consigliamo la chiamata telefonica: è il modo più rapido. Per richieste non urgenti puoi usare il modulo."
            />
            <Reveal delay={0.15}>
              <div className="mt-9 space-y-3.5">
                {CONTACT_CARDS.map((card) => (
                  <a
                    key={card.id}
                    href={card.href}
                    data-testid={`contact-card-${card.id}`}
                    onClick={card.onClick}
                    className="flex items-center gap-4 rounded-2xl border border-white/[0.08] bg-[#111620] p-4 transition-all duration-300 hover:border-amber-400/40 hover:bg-[#141a26]"
                  >
                    <span
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border ${card.accent}`}
                    >
                      <card.icon className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="block font-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                        {card.label}
                      </span>
                      <span className="block font-display text-base font-bold text-zinc-100">
                        {card.value}
                      </span>
                    </span>
                  </a>
                ))}
                <div className="flex items-center gap-4 rounded-2xl border border-white/[0.08] bg-[#111620] p-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/5 text-zinc-300">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      Zona servita
                    </span>
                    <span className="block font-display text-base font-bold text-zinc-100">
                      Roma e provincia
                    </span>
                  </span>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="rounded-3xl border border-white/[0.08] bg-[#111620] p-6 sm:p-8"
            >
              <p className="font-tech text-[11px] uppercase tracking-[0.25em] text-amber-400">
                Modulo richiesta assistenza
              </p>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-nome" className="mb-1.5 block text-xs font-semibold text-zinc-400">
                    Nome e Cognome *
                  </label>
                  <input
                    id="cf-nome"
                    data-testid="contact-form-nome-input"
                    type="text"
                    required
                    minLength={2}
                    placeholder="Mario Rossi"
                    value={form.nome}
                    onChange={update("nome")}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cf-telefono" className="mb-1.5 block text-xs font-semibold text-zinc-400">
                    Recapito telefonico *
                  </label>
                  <input
                    id="cf-telefono"
                    data-testid="contact-form-telefono-input"
                    type="tel"
                    required
                    minLength={5}
                    placeholder="333 1234567"
                    value={form.telefono}
                    onChange={update("telefono")}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cf-zona" className="mb-1.5 block text-xs font-semibold text-zinc-400">
                    Zona / Comune *
                  </label>
                  <input
                    id="cf-zona"
                    data-testid="contact-form-zona-input"
                    type="text"
                    required
                    minLength={2}
                    placeholder="Es. Roma oppure un comune della provincia"
                    value={form.zona}
                    onChange={update("zona")}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cf-servizio" className="mb-1.5 block text-xs font-semibold text-zinc-400">
                    Tipologia di problema *
                  </label>
                  <select
                    id="cf-servizio"
                    data-testid="contact-form-servizio-select"
                    required
                    value={form.servizio}
                    onChange={update("servizio")}
                    className={`${inputClass} ${form.servizio ? "" : "text-zinc-600"}`}
                  >
                    <option value="" disabled>
                      Seleziona il servizio
                    </option>
                    {SERVIZIO_OPTIONS.map((opt) => (
                      <option key={opt} value={opt} className="text-zinc-100">
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-messaggio" className="mb-1.5 block text-xs font-semibold text-zinc-400">
                    Descrizione del problema
                  </label>
                  <textarea
                    id="cf-messaggio"
                    data-testid="contact-form-messaggio-textarea"
                    rows={4}
                    placeholder="Descrivi cosa succede: il motore non gira, la serranda è bloccata, la chiave non gira, è saltata la corrente..."
                    value={form.messaggio}
                    onChange={update("messaggio")}
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>
              <button
                type="submit"
                data-testid="contact-form-submit-button"
                disabled={sending}
                className="mt-6 flex w-full items-center justify-center gap-3 rounded-full bg-amber-400 px-8 py-4 text-base font-extrabold uppercase tracking-wide text-zinc-950 transition-all hover:bg-amber-300 hover:shadow-[0_0_36px_rgba(245,158,11,0.35)] active:scale-[0.99] disabled:opacity-60"
              >
                {sending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
                {sending ? "Invio in corso..." : "Invia richiesta"}
              </button>
              <p className="mt-4 text-center text-xs text-zinc-500">
                Per urgenze immediate non attendere la risposta al modulo:{" "}
                <a
                  href={TEL_HREF}
                  data-testid="contact-form-call-link"
                  className="font-semibold text-amber-400 hover:text-amber-300"
                >
                  chiama il {SITE.phoneDisplay}
                </a>
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
